import { useMutation, useQuery } from '@tanstack/react-query'

import {
  mcpConsentApprove,
  mcpConsentDeny,
  mcpConsentRetrieveOptions
} from 'src/api/main'
import { useShowApiErrors } from 'src/utils'

import { isConsentExpiredError, isSafeConsentRedirect } from './consentModel'

/** Core returned a redirect Account refuses to follow; the page shows an error. */
export class UnsafeConsentRedirectError extends Error {}

export const useMcpConsent = (requestId: string) =>
  useQuery({
    ...mcpConsentRetrieveOptions({ path: { request_id: requestId } }),
    enabled: !!requestId,
    retry: false,
    refetchOnWindowFocus: false
  })

type Decision =
  | { decision: 'approve'; organizationId: number; scopes: string[] }
  | { decision: 'deny' }

export const useMcpConsentDecision = (requestId: string) => {
  const showApiErrors = useShowApiErrors()

  return useMutation({
    mutationFn: (input: Decision) =>
      (input.decision === 'approve'
        ? mcpConsentApprove({
            path: { request_id: requestId },
            body: {
              organization_id: input.organizationId,
              scopes: input.scopes
            },
            throwOnError: true
          })
        : mcpConsentDeny({
            path: { request_id: requestId },
            throwOnError: true
          })
      ).then(({ data }) => {
        if (!isSafeConsentRedirect(data.redirect_to)) {
          throw new UnsafeConsentRedirectError()
        }

        return data
      }),
    onSuccess: ({ redirect_to }) => window.location.assign(redirect_to),
    // Expired and refused redirects are shown by the page itself.
    onError: (error) => {
      if (
        !isConsentExpiredError(error) &&
        !(error instanceof UnsafeConsentRedirectError)
      ) {
        showApiErrors(error)
      }
    }
  })
}
